import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SpreadsheetItemInput from "../select/SpreadsheetItemInput";
import { useItemCache } from "../context/ItemCacheContext";
import { useSubprocesses, SubprocessOption } from "../select/SubprocessCells";
import { formatQtyForDisplay, parseDecimalInput } from "../utils/decimalInput";
import useSpreadsheetKeyboard from "../hooks/useSpreadsheetKeyboard";
import { Trash2 } from "lucide-react";
import OSCard, { OS_TH_CLS, OS_PLAIN_DISABLED_CLS } from "./OSCard";

// Меѓу/Полупроизвод table, controlled by OS.tsx and persisted per ident to
// Ingredient.interProducts. Uses the shared spreadsheet keyboard nav (arrow keys
// across cells + Enter/↓ to add a row) and the canonical blue focus accent, as
// the other OS spreadsheets. Only refs + quantity are saved; ident/name/ЕМ are
// derived from the item. Structure mirrors OSRazmerTable.tsx.

export interface SubprocessItemRow {
  accountingTypeCode?: string;
  /** Item's accounting-type group — see MatItem.accountingTypeGroup (same convention). */
  accountingTypeGroup?: string;
  subprocessId: string;
  issueSubprocessId: string;
  itemId: string;
  ident: string;
  name: string;
  quantity: string;
  unitCode: string;
  secondaryQty: string;
  secondaryUnitCode: string;
  allowedWaste: string;
  allowedYield: string;
  // ── Below: additive, RN-only fields. /os never reads or writes them (OS's own
  // table markup above has no columns for any of these) — same convention as
  // OSMaterialnoTable's MatItem, which carries its RN actuals as optional fields on
  // the very same row type OS renders read-only-blank. Keeping ONE row type (instead
  // of a parallel RN-only interface) is what avoids the dead-twin-table trap this
  // file's own header comment warns about.
  /** interProducts[] subdocument _id — RN's rowKey into interProductActuals[]. Never set by OS. */
  rowId?: string;
  /** interProducts[].priority — stable per-ident sort key. Display only in RN. */
  priority?: string;
  /** interProducts[].fromPct / .toPct — RN displays them read-only; semantics unresolved (§4.5 of the RN 7201 spec), never gated on. */
  fromPct?: string;
  toPct?: string;
  // RN "Материјално прием" (7201) actuals — measured RECEIVED quantity of this
  // intermediate. Same shape/semantics as MatItem's actuals; server-stamped
  // workedBy*/verifiedBy* are never written by the client.
  measuredQty?: string;
  serialNumber?: string;
  expiryDate?: string;
  waste?: string;
  rastur?: string;
  uzurkuvanje?: string;
  deviationReason?: string;
  workedByName?: string;
  workedAt?: string;
  workedBy?: string | { _id?: string };
  verifiedByName?: string;
  verifiedAt?: string;
  verifiedBy?: string | { _id?: string };
  verifyRequested?: boolean;
  confirmed?: boolean;
}

export const emptySubprocessItemRow = (): SubprocessItemRow => ({
  subprocessId: "", issueSubprocessId: "", itemId: "", ident: "", name: "", quantity: "", unitCode: "",
  secondaryQty: "", secondaryUnitCode: "", allowedWaste: "", allowedYield: "",
});

interface Props {
  rows: SubprocessItemRow[];
  onChange: (rows: SubprocessItemRow[]) => void;
  chainSubprocessIds?: string[];
  readOnly?: boolean;
}

const unitCodeOf = (item: any): string => {
  const u = item.mainUnit || item.summaryUnit || item.unitMeasurment || item.unit;
  if (!u) return "";
  return typeof u === "object" ? (u.code || u.unitMeasurment || u.name || "") : "";
};

const secondaryUnitCodeOf = (item: any): string => {
  const u = item.secondaryUnit;
  if (!u) return "";
  return typeof u === "object" ? (u.code || u.unitMeasurment || u.name || "") : "";
};

// Меѓу/Полупроизводот нема суровински/амбалажни класи — неговиот отпад е на видот МП/ПП
// (mpPpType). Огледало на resolveAllowedWasteFromItem во server/routes/ingredients.js.
const allowedWasteOf = (item: any): string => {
  for (const c of [item?.rawMaterialType, item?.paType, item?.saType, item?.mpPpType]) {
    if (c && typeof c === "object" && c.allowedWaste != null) {
      return String(c.allowedWaste);
    }
  }
  return "";
};

// Подпроцес прием доаѓа од Видот на Меѓу/Полупроизвод (`mpPpType`) на идентот — истиот образец
// како allowedWasteOf. Полето може да е сиров ObjectId или populate-иран објект.
// Подпроцес издавање НЕ доаѓа од mpPpType — се изведува од синџирот на редови (види
// deriveIssueSubprocessIds подолу).
const subprocessRefOf = (item: any, key: "receiptSubprocess"): string => {
  const v = item?.mpPpType?.[key];
  if (!v) return "";
  return typeof v === "object" ? String(v._id || "") : String(v);
};

// Издавањето на секој ред зависи од ПРОИЗВОДОТ (синџирот на редови во ОС), не од видот на
// идентот — два реда со ист прием можат да имаат различно издавање ако конвергираат подоцна
// (пр. два гранулата се блендираат заедно). Затоа не земаме буквално "следен ред", туку првиот
// НАРЕДЕН ред (по редослед на процес/подпроцес) чиј подпроцес се разликува од мојот.
//
// Забелешка: серверот е авторитет и ја снима истата вредност во Ingredient.interProducts при
// снимање (normalizeInterProducts) — ова е само жив приказ додека корисникот уредува во ОС.
const orderOf = (subprocessId: string, byId: Map<string, SubprocessOption>): [number, number] => {
  const sp = byId.get(subprocessId);
  if (!sp) return [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];
  const pd = sp.processDefinition;
  const pdOrder = pd && typeof pd === "object" ? pd.order ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
  return [pdOrder, sp.order ?? Number.MAX_SAFE_INTEGER];
};

// Стабилно сортирање по (processDefinition.order, subprocess.order), tie-break по оригиналниот
// индекс. Издвоено зашто и deriveFinishedReceiptSubprocessId мора да го користи ИСТИОТ редослед —
// инаку „последниот ред" за готовиот производ би бил друг ред од оној што го гледа табелата.
const sortRowIndicesByChain = (
  rows: Pick<SubprocessItemRow, "subprocessId">[],
  byId: Map<string, SubprocessOption>
): number[] =>
  rows.map((_, idx) => idx).sort((a, b) => {
    const [aPd, aSp] = orderOf(rows[a].subprocessId, byId);
    const [bPd, bSp] = orderOf(rows[b].subprocessId, byId);
    if (aPd !== bPd) return aPd - bPd;
    if (aSp !== bSp) return aSp - bSp;
    return a - b;
  });

// Синџирот подпроцеси на ОС (левото дрво / selectedSubprocessIds), подреден по ИСТИОТ
// (processDefinition.order, subprocess.order) критериум како редовите — не по редоследот во
// влезната низа, тој е артефакт на тоа што прво се пополнило, не намера.
//
// Издвоено зашто ГО КОРИСТАТ ДВЕ функции: deriveIssueSubprocessIds (tier-2 fallback за ред без
// нареден ред) и deriveFinishedReceiptSubprocessId (кога воопшто нема редови). Ако се разидат,
// „последниот подпроцес во синџирот" би значел различно нешто во двете — токму разидувањето
// што sortRowIndicesByChain го спречува за редовите.
const sortChainSubprocessIds = (
  chainSubprocessIds: string[],
  byId: Map<string, SubprocessOption>
): string[] =>
  [...chainSubprocessIds].sort((a, b) => {
    const [aPd, aSp] = orderOf(a, byId);
    const [bPd, bSp] = orderOf(b, byId);
    if (aPd !== bPd) return aPd - bPd;
    if (aSp !== bSp) return aSp - bSp;
    return 0;
  });

export const deriveIssueSubprocessIds = (
  rows: Pick<SubprocessItemRow, "subprocessId">[],
  subprocesses: SubprocessOption[],
  chainSubprocessIds: string[] = []
): string[] => {
  const byId = new Map(subprocesses.map((sp) => [sp._id, sp]));

  const sortedIndices = sortRowIndicesByChain(rows, byId);

  // Fallback синџир: се користи само за редови на чиј подпроцес не му се најде нареден ред со
  // различен подпроцес (типично последниот ред во синџирот — тој подпроцес произведува готов
  // производ, не меѓупроизвод, па нема свој ред, но следниот подпроцес во ОС сепак го троши
  // неговиот резултат).
  const sortedChain = sortChainSubprocessIds(chainSubprocessIds, byId);

  const derived: string[] = new Array(rows.length).fill("");
  for (let pos = 0; pos < sortedIndices.length; pos++) {
    const rowIdx = sortedIndices[pos];
    const mySubprocess = rows[rowIdx].subprocessId;
    let found = false;
    for (let next = pos + 1; next < sortedIndices.length; next++) {
      const candidateSubprocess = rows[sortedIndices[next]].subprocessId;
      if (candidateSubprocess && candidateSubprocess !== mySubprocess) {
        derived[rowIdx] = candidateSubprocess;
        found = true;
        break;
      }
    }
    if (!found && mySubprocess) {
      const chainPos = sortedChain.indexOf(mySubprocess);
      if (chainPos !== -1 && chainPos + 1 < sortedChain.length) {
        derived[rowIdx] = sortedChain[chainPos + 1];
      }
    }
  }
  return derived;
};

// Подпроцес прием на ГОТОВИОТ ПРОИЗВОД = издавањето на ПОСЛЕДНИОТ ред во синџирот
// Меѓу/Полупроизвод. Готовиот производ се прима таму каде последниот меѓупроизвод се издава
// (пр. Блистирање издава на Картонирање → готовата кутија се прима на Картонирање).
//
// Намерно ја вика deriveIssueSubprocessIds и го користи ИСТИОТ редослед на сортирање, за да не
// може ќелијата во картичката Готов Производ да се разиде од ќелијата „Подпроцес издавање" во
// табелата под неа. `readOnly` го огледува истото правило како самата табела: во РН серверот е
// авторитет (снимената вредност прва), во ОС се гледа живата деривација.
//
// ФАЛЛБЕК КОГА НЕМА НИТУ ЕДЕН РЕД: норматив со Размерување, но без Меѓу/Полупроизвод, е сосема
// валиден — еднофазен производ (пр. само Блендирање) не предава ништо натаму, неговиот излез Е
// готовиот производ. Дотогаш оваа функција враќаше "" во тој случај, па ќелијата „Подпроцес
// прием" во Готов Производ остануваше празна засекогаш, иако одговорот е очигледен: последниот
// подпроцес во синџирот на нормативот. Истата логика важи и кога има повеќе фази, но табелата
// уште не е пополнета.
//
// Ова НЕ го менува однесувањето кога постои барем еден пополнет ред — тогаш циклусот враќа пред
// да се стигне дотука. Во РН (`readOnly`) синџирот не се проследува (chainSubprocessIds = []),
// па фаллбекот е празен и таму сè останува исто.
export const deriveFinishedReceiptSubprocessId = (
  rows: (Pick<SubprocessItemRow, "subprocessId">
    & Partial<Pick<SubprocessItemRow, "issueSubprocessId" | "itemId">>)[],
  subprocesses: SubprocessOption[],
  chainSubprocessIds: string[] = [],
  readOnly = false
): string => {
  const byId = new Map(subprocesses.map((sp) => [sp._id, sp]));
  const lastInChain = (): string => {
    const sortedChain = sortChainSubprocessIds(chainSubprocessIds, byId);
    return sortedChain.length > 0 ? sortedChain[sortedChain.length - 1] : "";
  };
  if (!rows.length) return lastInChain();
  const sortedIndices = sortRowIndicesByChain(rows, byId);
  const derived = deriveIssueSubprocessIds(rows, subprocesses, chainSubprocessIds);
  // Одзади наназад: празните редови на дното (spreadsheet секогаш држи еден празен) не се
  // крајот на синџирот.
  for (let pos = sortedIndices.length - 1; pos >= 0; pos--) {
    const idx = sortedIndices[pos];
    if (!rows[idx].subprocessId && !rows[idx].itemId) continue;
    return readOnly ? (rows[idx].issueSubprocessId || derived[idx]) : derived[idx];
  }
  // Сите редови се празни — истиот случај како rows.length === 0.
  return lastInChain();
};

// Внес од корисник — заеднички парсер со сите OS табели. Види `utils/decimalInput.ts`.
const parseNum = parseDecimalInput;

// Дозволен принос е КОЛИЧИНА во ЕМ → МК децимален знак (запирка), БЕЗ групирање на
// илјади, за да може да се прочита назад со `parseDecimalInput` без двосмисленост.
const formatNum = (value: number): string =>
  formatQtyForDisplay(value, { showZero: true });

export const allowedYieldOf = (row: Pick<SubprocessItemRow, "quantity" | "allowedWaste" | "allowedYield">): string => {
  const qty = parseNum(row.quantity);
  const waste = parseNum(row.allowedWaste);
  if (qty != null && waste != null) return formatNum(qty * (1 - waste / 100));
  return row.allowedYield || "";
};

// `width`, not `minWidth` — заедно со `table-fixed` подолу. Оваа табела и „Готов Производ"
// стојат една врз друга и носат ИДЕНТИЧЕН низ ширини, за колона N долу да се поклопи со колона
// N горе. Ако менуваш ширина овде, смени ја и во FinishedProductCard.
const TH: React.FC<{ w: number; title?: string; children: React.ReactNode }> = ({ w, title, children }) => (
  <th
    className={OS_TH_CLS}
    style={{ width: w }}
    title={title}
  >
    {children}
  </th>
);

// Canonical blue focus accent (CLAUDE.md hard rule — the yellow variant these carried is the
// known accent bug, not a style choice).
const cellBase = `w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${OS_PLAIN_DISABLED_CLS}`;

const OSSubprocessItemsTable: React.FC<Props> = ({ rows, onChange, chainSubprocessIds = [], readOnly = false }) => {
  const { t } = useTranslation();
  const { getItem, fetchItemById } = useItemCache();
  const subprocesses = useSubprocesses();
  const inputRefs = useRef<(HTMLInputElement | HTMLSelectElement | null)[][]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Controlled table — see the same note in OSRazmerTable: handlers must rebuild from the
  // LATEST rows, not from the `rows` captured when the handler was created, or selectMain's
  // background item fetch resolves onto a stale array and drops edits made while it was in
  // flight.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // readOnly е RN (/work-order/:code): таму оваа табела е ПРИКАЗ на планот што доаѓа од OS —
  // RN никогаш не смее да запише во `Ingredient.interProducts`. Денес ниту една ќелија во
  // readOnly не е фокусабилна, па ова е недостижно; заштитата е тука за да остане недостижно
  // и кога некој утре ќе додаде фокусабилна ќелија. RN-овиот onChange вика setIsDirty(true),
  // значи без ова еден невнимателен ред би му го извалкал формуларот и би снимил погрешен shape.
  const patch = (i: number, p: Partial<SubprocessItemRow>) => {
    if (readOnly) return;
    onChange(rowsRef.current.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  };

  const setRows = (u: React.SetStateAction<SubprocessItemRow[]>) => {
    if (readOnly) return;
    onChange(typeof u === "function" ? (u as (p: SubprocessItemRow[]) => SubprocessItemRow[])(rowsRef.current) : u);
  };
  const hasRowData = (r: SubprocessItemRow) => !!(r.subprocessId || r.issueSubprocessId || r.itemId || r.quantity || r.secondaryQty || r.allowedWaste || r.allowedYield);
  const { handleKeyDown } = useSpreadsheetKeyboard({ rows, setRows, inputRefs, createEmptyRow: emptySubprocessItemRow, hasRowData });
  const isAllSelected = rows.length > 0 && selectedRows.size === rows.length;
  const isSomeSelected = selectedRows.size > 0;

  const toggleAll = () => {
    if (readOnly) return;
    setSelectedRows(isAllSelected ? new Set() : new Set(rows.map((_, i) => i)));
  };

  const toggleRow = (i: number) => {
    if (readOnly) return;
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const deleteSelected = () => {
    if (readOnly || selectedRows.size === 0) return;
    const kept = rows.filter((_, i) => !selectedRows.has(i));
    onChange(kept.length === 0 ? [emptySubprocessItemRow()] : kept);
    setSelectedRows(new Set());
    inputRefs.current = [];
  };

  // Живо изведено издавање по ред — се преизвршува штом корисникот додаде/избрише/промени ред.
  const derivedIssueSubprocessIds = useMemo(
    () => deriveIssueSubprocessIds(rows, subprocesses, chainSubprocessIds),
    [rows, subprocesses, chainSubprocessIds]
  );

  const reg = (i: number, col: number) => ({
    ref: (el: HTMLInputElement | HTMLSelectElement | null) => {
      if (!inputRefs.current[i]) inputRefs.current[i] = [];
      inputRefs.current[i][col] = el;
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => handleKeyDown(e, i, col),
  });

  const populateFromItem = (item: any): Partial<SubprocessItemRow> => ({
    ident: item.ident || "",
    name: item.name || "",
    unitCode: unitCodeOf(item),
    secondaryUnitCode: secondaryUnitCodeOf(item),
    allowedWaste: allowedWasteOf(item),
    allowedYield: "",
    subprocessId: subprocessRefOf(item, "receiptSubprocess"),
  });

  const selectMain = (i: number, id: string) => {
    if (!id) {
      patch(i, { itemId: "", ident: "", name: "", unitCode: "", secondaryUnitCode: "", allowedWaste: "", allowedYield: "", subprocessId: "", issueSubprocessId: "" });
      return;
    }
    // First try cache (instant) - items are already cached from dropdown search
    const item = getItem(id);
    if (item) {
      patch(i, { itemId: id, ...populateFromItem(item) });
    } else {
      // Fetch from API in background if not in cache
      patch(i, { itemId: id });
      fetchItemById(id).then((fetchedItem) => {
        if (fetchedItem) {
          patch(i, { itemId: id, ...populateFromItem(fetchedItem) });
        }
      }).catch(() => {
        // Item fetch failed silently - non-critical
      });
    }
  };

  return (
    <OSCard
      title="Меѓу/Полупроизвод"
      actions={!readOnly && isSomeSelected && (
        <button
          type="button"
          onClick={deleteSelected}
          className="flex items-center gap-1 text-white/90 hover:text-white text-xs font-medium px-2 py-1 rounded hover:bg-white/10 transition-colors"
          title={`Избриши ${selectedRows.size} избрани редови`}
        >
          <Trash2 size={14} />
          Избриши ({selectedRows.size})
        </button>
      )}
    >
      <div className="overflow-x-auto">
        <table className="border-collapse w-full table-fixed">
          <thead>
            <tr>
              {!readOnly && (
                <>
                  <th className="border border-gray-300 bg-gray-200 w-6 h-[26px] px-1">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(el) => { if (el) el.indeterminate = isSomeSelected && !isAllSelected; }}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="border border-gray-300 bg-gray-200 w-6 h-[26px] px-1">
                    <button
                      type="button"
                      onClick={deleteSelected}
                      disabled={!isSomeSelected}
                      className={`${isSomeSelected ? "text-red-500 hover:text-red-700 cursor-pointer" : "text-gray-300 cursor-not-allowed"} transition-colors`}
                      title={isSomeSelected ? `Избриши ${selectedRows.size} избрани редови` : "Нема избрани редови"}
                    >
                      <Trash2 size={14} className="mx-auto" />
                    </button>
                  </th>
                </>
              )}
              <TH w={100}>Прием</TH>
              <TH w={100}>Издавање</TH>
              <TH w={120}>Шифра</TH>
              <TH w={400}>Назив</TH>
              <TH w={58}>Количина</TH>
              {readOnly && <TH w={100}>{t("os.materialno.priemColReceivedQty")}</TH>}
              <TH w={36}>ЕМ</TH>
              <TH w={68} title="Количина споредна ЕМ">Кол. спор. ЕМ</TH>
              <TH w={46} title="Споредна ЕМ">Спор. ЕМ</TH>
              {/* Експлицитен прелом на два реда — исто како во FinishedProductCard, за да
                  изгледаат идентично во двете табели што стојат една врз друга. */}
              <TH w={100}>Дозволен принос<br />(ЕМ)</TH>
              <TH w={90}>Дозволен отпад<br />(%)</TH>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              // Во readOnly режим (RN) серверот е авторитет за издавањето — веќе снимено на
              // row.issueSubprocessId (normalizeInterProducts). RN не проследува chainSubprocessIds,
              // па живото изведување би го погрешило последниот ред во синџирот; затоа само fallback.
              const issueId = readOnly ? (row.issueSubprocessId || derivedIssueSubprocessIds[i]) : derivedIssueSubprocessIds[i];
              return (
              <tr key={i} className="hover:bg-blue-50 focus-within:bg-blue-50">
                {!readOnly && (
                  <>
                    <td className="border border-gray-300 h-[26px] px-1 text-center">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(i)}
                        onChange={() => toggleRow(i)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="border border-gray-300 h-[26px] px-1 text-center text-[11px] text-gray-500">
                      {i + 1}
                    </td>
                  </>
                )}
                {readOnly ? (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                    {subprocesses.find((sp) => sp._id === row.subprocessId)?.name || ""}
                  </td>
                ) : (
                  <td className="border border-gray-300 h-[26px] px-0.5 py-0.5">
                    <select
                      className={`${cellBase} disabled:appearance-none`}
                      value={row.subprocessId}
                      onChange={(e) => patch(i, { subprocessId: e.target.value, issueSubprocessId: "" })}
                      {...reg(i, 0)}
                    >
                      <option value=""></option>
                      {subprocesses.map((sp) => (
                        <option key={sp._id} value={sp._id}>{sp.name}</option>
                      ))}
                    </select>
                  </td>
                )}
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                  {subprocesses.find((sp) => sp._id === issueId)?.name || ""}
                </td>
                {readOnly ? (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                    {row.ident}
                  </td>
                ) : (
                  <td className="border border-gray-300 h-[26px] px-0.5 py-0.5">
                    <SpreadsheetItemInput
                      value={row.itemId}
                      displayValue={row.ident}
                      displayBy="code"
                      accountingTypeGroup="semi-finished,intermediate"
                      size="tablePlain"
                      enableValidation={true}
                      enableRef
                      onChange={(id) => selectMain(i, id)}
                      onSelect={() => inputRefs.current[i]?.[2]?.focus()}
                      {...reg(i, 1)}
                    />
                  </td>
                )}
                {readOnly ? (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-left text-[11px] text-gray-700">
                    {row.name}
                  </td>
                ) : (
                  <td className="border border-gray-300 h-[26px] px-0.5 py-0.5">
                    <SpreadsheetItemInput
                      value={row.itemId}
                      displayValue={row.name}
                      displayBy="name"
                      accountingTypeGroup="semi-finished,intermediate"
                      size="tablePlain"
                      enableValidation={false}
                      enableRef
                      onChange={(id) => selectMain(i, id)}
                      onSelect={() => inputRefs.current[i]?.[3]?.focus()}
                      {...reg(i, 2)}
                    />
                  </td>
                )}
                {readOnly ? (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700">
                    {row.quantity}
                  </td>
                ) : (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                    <input className={`${cellBase} text-right tabular-nums`} value={row.quantity} onChange={(e) => patch(i, { quantity: e.target.value })} {...reg(i, 3)} />
                  </td>
                )}
                {readOnly && (
                  <td
                    className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px]${row.confirmed === true ? " text-gray-700" : " text-gray-400"}`}
                    title={row.confirmed === true ? undefined : t("os.materialno.priemNotConfirmed")}
                  >
                    {formatQtyForDisplay(parseDecimalInput(row.measuredQty))}
                  </td>
                )}
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                  {row.unitCode}
                </td>
                {readOnly ? (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700">
                    {row.secondaryQty}
                  </td>
                ) : (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                    <input className={`${cellBase} text-right tabular-nums`} value={row.secondaryQty} onChange={(e) => patch(i, { secondaryQty: e.target.value })} {...reg(i, 4)} />
                  </td>
                )}
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                  {row.secondaryUnitCode}
                </td>
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700">
                  {allowedYieldOf(row)}
                </td>
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700">
                  {row.allowedWaste}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </OSCard>
  );
};

export default OSSubprocessItemsTable;
